import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  BulkCreateProjectDocumentsDocument,
  ProjectDocumentsDataCollectionDocument,
  ProjectManagementProjectDetailsPageDocumentsPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EmptyState from 'modules/project_management/components/EmptyState'
import ProjectDocumentsDataCollection from 'modules/project_management/components/tables/ProjectDocumentsDataCollection'
import { ProjectsUpsellRequestSources } from 'modules/project_management/components/upsells/constants'
import DocumentsUploadWizard from 'modules/project_management/components/wizards/AddDocumentWizard/DocumentsUploadWizard'
import documentsBackground from 'modules/project_management/lib/assets/upsell-documents.svg'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

const DocumentsPage = () => {
  const { id: projectId } = useParams()
  const { employee } = useCurrentSession()
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    track(trackingEvents.projectManagement.projectFiles.visitProjectFilesTab)
  }, [])

  const [createProjectDocument, { loading: createProjectDocumentLoading }] = useMutation(
    BulkCreateProjectDocumentsDocument,
    {
      extractorFn: (mutation) =>
        mutation.projectManagementMutations.bulkCreateProjectmanagementProjectDocument,
      refetchQueries: [ProjectDocumentsDataCollectionDocument],
      awaitRefetchQueries: true,
    }
  )

  const handleSuccess = async (files: File[]) => {
    if (!projectId || !employee?.id) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('project_management.files.upload_error'),
      })
      return
    }

    const result = await createProjectDocument({
      variables: {
        files,
        projectId: Number(projectId),
        uploaderId: employee.id,
      },
    })

    track(trackingEvents.projectManagement.projectFiles.clickOnUploadConfirmation)
    setShowModal(false)

    app.setFlash({
      type: 'correct',
      message: i18n.t('project_management.files.upload_success'),
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('project_management.files.upload_success'),
      })
      setShowModal(false)
    }
  }

  const { hasPayingProjectsFeature } = useOutletContext<{
    hasPayingProjectsFeature: boolean
  }>()

  const { data } = useQuery(ProjectManagementProjectDetailsPageDocumentsPageDocument, {
    variables: {
      projectId: Number(projectId),
    },
  })

  const isProjectDocumentsEmpty = false
  const canManageDocuments = data?.projectManagement.project?._policies?.assignEditor.can || false
  const handleOnUploadClick = () => {
    setShowModal(true)
  }

  if (!hasPayingProjectsFeature) {
    return (
      <Box style={{ height: 'calc(100vh - 300px)' }}>
        <UpsellingKit.EmptyState
          moduleRequested={AvailableModules.projects}
          requestSource={ProjectsUpsellRequestSources.projectsDocuments}
          requestReason={ValidReasons.MissingAddon}
          learnMoreNavigate="https://help.factorialhr.com/tasks-projects"
          background={documentsBackground}
          title={i18n.t('project_management.files.upselling.title')}
          description={i18n.t('project_management.files.upselling.description')}
        />
      </Box>
    )
  }

  return (
    <>
      <Box gap="s24">
        {isProjectDocumentsEmpty ? (
          <EmptyState
            title={i18n.t('project_management.files.empty_state.title')}
            description={i18n.t('project_management.files.empty_state.description')}
            emoji={'📄'}
            buttonText={i18n.t('project_management.files.empty_state.button_text')}
            buttonAction={() => setShowModal(true)}
          />
        ) : (
          <ProjectDocumentsDataCollection
            projectId={Number(projectId)}
            canManageDocuments={canManageDocuments}
            onClickUpload={handleOnUploadClick}
          />
        )}
      </Box>

      {showModal && (
        <DocumentsUploadWizard
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
          loading={createProjectDocumentLoading}
        />
      )}
    </>
  )
}

export default DocumentsPage
